import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { Link, useBlocker, useSearchParams } from 'react-router-dom';
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  AlignCenterHorizontal,
  AlignCenterVertical,
  ArrowLeft,
  ArrowRight,
  Check,
  Crosshair,
  Link2,
  SkipForward,
  Undo2,
} from 'lucide-react';
import {
  Button,
  Card,
  EmptyState,
  Input,
  LoadingState,
  StatusPill,
} from '@/components/ui';
import { getErrorMessage } from '@/api';
import {
  CalibrationStatusPill,
  MM_FIELD_LABEL,
  NotSelectableBanner,
  PrintAreaEditor,
  buildDraft,
  calibrationErrorMessage,
  calibrationSavedMessage,
  draftsEqual,
  editorCutoutMaskUrl,
  frameFromDrawn,
  frameSourceOf,
  frameStatusOf,
  missingMmField,
  SAMPLE_DESIGN_RATIO,
  type EditableArea,
  type PrintAreaDraft,
  type SampleDesignBox,
} from '@/components/admin';
import { centerArea, type AreaBounds } from '@/components/admin/printAreaMath';
import {
  effectiveCarryOver,
  fitIntoBox,
  fitIntoRect,
  targetPercentBox,
  templateFrameStatus,
  type CarryOverAnchor,
  type CarryOverBox,
  type RelativeDesignState,
} from '@designdeck/shared';
import {
  useAdminProduct,
  useCalibrateTemplate,
  useSetProductCarryOver,
} from '@/hooks/useAdminProducts';
import MockupRenderer from '@/components/editor/MockupRenderer';
import {
  proxyStorageUrl,
  transformMockupLayers,
} from '@/lib/product-transformer';
import { isEmbroideryProduct } from '@/lib/decoration-method';
import * as adminApi from '@/api/admin';
import { buildEntries, inFixedOrder, isTemplateOpen } from './frameReviewQueue';
import { FrameReviewFilterBar } from './FrameReviewFilterBar';
import {
  parseFrameReviewFilters,
  toFrameReviewQuery,
  writeFrameReviewFilters,
  type FrameReviewFilters,
} from './frameReviewFilters';
import { linkSku, resolveLinkedProduct } from './frameReviewLink';

/** "vor 3 Tagen" / "heute" / "nie" for the product's last frame save. */
function frameChangedLabel(iso: string | null, now = Date.now()): string {
  if (!iso) return 'nie';
  const days = Math.floor((now - new Date(iso).getTime()) / 86_400_000);
  if (days <= 0) return 'heute';
  if (days === 1) return 'gestern';
  return `vor ${days} Tagen`;
}

// ═══════════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════════

/** Check colour the garment is painted in by default: neon green sets the
 * garment off from the stage and from any real product colour, so the
 * contour and the frame stand out. It is offered next to the product's own
 * colours and is kept across products, like "ohne Farbe". */
const NEON_COLOR = {
  id: 'neon',
  name: 'Neon-Grün (Prüffarbe)',
  hexCode: '#39FF14',
  isHeathered: false,
} as const;
/** Stage around a masked garment: a light neutral, so the colour sits on
 * the garment alone. Without a mask the colour fills the stage instead. */
const NEUTRAL_STAGE_BG = '#F4F4F5';
/** Stable empty queue, so effects keyed on `products` do not re-run per render. */
const EMPTY_QUEUE: readonly adminApi.FrameReviewQueueItem[] = [];

/**
 * Where the creator would put the sample artwork in this frame, in area
 * pixels: fitted into the carry-over percent box, or into the whole area
 * without a percent rule. The outline then sits on the motif itself, like
 * the creator's selection frame, not on the box around it.
 */
function sampleDesignRect(
  draft: Pick<PrintAreaDraft, 'width' | 'height' | 'maxWidthMm'>,
  percentBox: CarryOverBox | null
): SampleDesignBox {
  const area = {
    width: draft.width,
    height: draft.height,
    maxWidthMm: draft.maxWidthMm,
  };
  const sample: RelativeDesignState = {
    relCenterX: 0.5,
    relCenterY: 0.5,
    relWidth: 1,
    designRatio: SAMPLE_DESIGN_RATIO,
  };
  const placement = percentBox
    ? fitIntoBox(sample, area, area, percentBox).placement
    : fitIntoRect(sample, area, {
        left: 0,
        top: 0,
        width: area.width,
        height: area.height,
      });
  const width = placement.relWidth * area.width;
  const height = width * placement.designRatio;
  return {
    left: placement.relCenterX * area.width - width / 2,
    top: placement.relCenterY * area.height - height / 2,
    width,
    height,
  };
}

const CHECKER_SWATCH_STYLE: CSSProperties = {
  backgroundImage:
    'linear-gradient(45deg, #bbb 25%, transparent 25%), linear-gradient(-45deg, #bbb 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #bbb 75%), linear-gradient(-45deg, transparent 75%, #bbb 75%)',
  backgroundSize: '8px 8px',
  backgroundPosition: '0 0, 0 4px, 4px -4px, -4px 0px',
  backgroundColor: '#fff',
};

// ═══════════════════════════════════════════════════════════════════
// PAGE
// ═══════════════════════════════════════════════════════════════════

function swatchClass(active: boolean): string {
  return `h-5 w-5 rounded-pill ring-2 ring-offset-1 ring-offset-paper transition-transform hover:scale-110 ${
    active ? 'ring-primary' : 'ring-line'
  }`;
}

/** One sidebar setting: label on the left, controls and a hint on the right. */
function SettingRow({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="flex items-start gap-2">
      <span className="w-24 shrink-0 pt-1 text-[11px] font-medium uppercase tracking-wide text-mute">
        {label}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">{children}</div>
        {hint && (
          <p className="mt-1 text-[11px] leading-snug text-mute">{hint}</p>
        )}
      </div>
    </div>
  );
}

/**
 * Frame-review walkthrough: step through the products' mockup templates one
 * at a time — default templates first, then the others, each front before
 * back, then the next product — and drag the design frame into place with
 * the magnet. By default it holds exactly the templates still to calibrate
 * (`open`). Each save calibrates the template on screen through the same
 * endpoint the per-product page uses (R10): width and position are the
 * admin's, the height follows from the millimetres.
 */
export function FrameReviewPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  // The filters live in the URL next to the link params; the queue is
  // fetched per filter set and the previous queue stays on screen while the
  // next one loads, so the filter bar never disappears.
  const filters = useMemo(
    () => parseFrameReviewFilters(searchParams),
    [searchParams]
  );
  const queueQuery = useMemo(() => toFrameReviewQuery(filters), [filters]);
  const {
    data: queue,
    isLoading: listLoading,
    isFetching: listFetching,
  } = useQuery({
    queryKey: ['frame-review', 'queue', queueQuery],
    queryFn: () => adminApi.getFrameReviewQueue(queueQuery),
    placeholderData: keepPreviousData,
  });
  const products = queue?.items ?? EMPTY_QUEUE;
  const queryClient = useQueryClient();
  const openTemplates = useMemo(
    () => products.reduce((sum, item) => sum + item.templatesToCalibrate, 0),
    [products]
  );
  const onlyOpen = filters.calibration === 'open';

  const [rawProductIndex, setProductIndex] = useState(0);
  // A shared link (?sku=…&mockup=…) to open first. It stays set until the
  // walkthrough stands on that mockup; until then the URL is not rewritten.
  const [pendingLink, setPendingLink] = useState<{
    sku: string;
    mockup: string | null;
    productId?: string;
  } | null>(() => {
    const sku = searchParams.get('sku');
    return sku ? { sku, mockup: searchParams.get('mockup') } : null;
  });
  const [entryIndex, setEntryIndex] = useState(0);

  // A new queue (filters changed, or a reload): stay on the product on
  // screen when it is still in, otherwise start the new queue at its first
  // product. This runs during render (React's adjust-state-on-prop-change
  // pattern) and this very pass already uses the new index, so nothing
  // downstream ever sees the product that happens to sit at the old index
  // in the new queue: not the draft rebuild below (unsaved edits would be
  // lost even though React re-runs the render, since its state updates
  // stick) and not the effects keyed on the product id, like the mockup
  // reset. The product on screen is read from the previous queue with the
  // index clamped to THAT queue.
  // A shared link still looking for its SKU re-runs against the new queue
  // on its own (step 1 below); one that already found its product keeps
  // that product, or is given up when the product dropped out.
  const [seenQueue, setSeenQueue] = useState(products);
  let queueIndex = rawProductIndex;
  if (seenQueue !== products) {
    setSeenQueue(products);
    if (!pendingLink || pendingLink.productId) {
      const previousIndex = Math.min(
        rawProductIndex,
        Math.max(seenQueue.length - 1, 0)
      );
      const id = pendingLink?.productId ?? seenQueue[previousIndex]?.id;
      const index = id ? products.findIndex((p) => p.id === id) : -1;
      queueIndex = index >= 0 ? index : 0;
      setProductIndex(queueIndex);
      if (pendingLink && index < 0) setPendingLink(null);
    }
  }
  // The index is clamped on read: a queue that shrinks underneath an
  // in-flight navigation must never leave the page without a product.
  const productIndex = Math.min(queueIndex, Math.max(products.length - 1, 0));
  const [draft, setDraft] = useState<PrintAreaDraft | null>(null);
  const [baseline, setBaseline] = useState<PrintAreaDraft | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Hint of the last calibration, kept while the walkthrough moves on.
  const [savedHint, setSavedHint] = useState<string | null>(null);
  // Colour the garment is previewed in (layer 0, like the creator).
  // `undefined` = neon green; neon and `null` (no colour layer) hold across
  // products, a product colour only for its own product.
  const [colorChoice, setColorChoice] = useState<
    { productId: string; colorId: string | null } | undefined
  >(undefined);

  const currentProduct = products[productIndex];
  const { data: product, isLoading: productLoading } = useAdminProduct(
    currentProduct?.id
  );

  // With `open`, the templates still to calibrate when the product was
  // opened (plus a mockup a shared link names). A template calibrated now
  // keeps its place until the admin leaves the product, so the steps do not
  // shift under the save; on the next visit it is gone.
  const snapshotKey = product ? `${product.id}:${onlyOpen}` : null;
  // The step order is fixed with it: each save reloads the product, and
  // the reload may list the templates of one position in another order.
  const [openSnapshot, setOpenSnapshot] = useState<{
    key: string;
    ids: ReadonlySet<string>;
    order: readonly string[];
  } | null>(null);
  if (product && snapshotKey && openSnapshot?.key !== snapshotKey) {
    const ids = new Set(
      buildEntries(product.positions, product.mockupTemplates, (e) =>
        isTemplateOpen(e.position, e.template)
      ).map((e) => e.template.id)
    );
    if (pendingLink?.mockup) ids.add(pendingLink.mockup);
    const order = buildEntries(product.positions, product.mockupTemplates).map(
      (e) => e.template.id
    );
    setOpenSnapshot({ key: snapshotKey, ids, order });
  }
  const snapshot =
    openSnapshot && openSnapshot.key === snapshotKey ? openSnapshot : null;
  const snapshotIds = snapshot?.ids ?? null;
  const snapshotOrder = snapshot?.order ?? null;
  const entries = useMemo(() => {
    const built = buildEntries(
      product?.positions,
      product?.mockupTemplates,
      onlyOpen
        ? (e) =>
            snapshotIds
              ? snapshotIds.has(e.template.id)
              : isTemplateOpen(e.position, e.template)
        : undefined
    );
    return snapshotOrder ? inFixedOrder(built, snapshotOrder) : built;
  }, [product, onlyOpen, snapshotIds, snapshotOrder]);
  const entry = entries[entryIndex];

  const mockupWidth = entry?.template.mockupWidthPx ?? 500;
  const mockupHeight = entry?.template.mockupHeightPx ?? 500;
  const isDefaultTemplate = entry ? entry.template.isDefault !== false : true;
  // Every template carries its own calibration (R6), so every frame can be
  // moved and resized; only the millimetres it follows are the position's.
  const frameSource = entry
    ? frameSourceOf(entry.position, entry.template)
    : 'suggestion';
  const frameStatus = entry
    ? frameStatusOf(entry.position, entry.template)
    : 'uncalibrated';

  const baseImageUrl = proxyStorageUrl(
    entry?.template.renderingImageUrl ||
      entry?.template.imageUrl ||
      product?.mockupImageUrl
  );

  // The mockup is drawn by the creator's own MockupRenderer, fed through the
  // same transformer, so colour, mask, heathered pattern, shading and
  // overlays follow exactly the creator's rules.
  const uiTemplate = useMemo(
    () => (entry ? transformMockupLayers(entry.template) : null),
    [entry]
  );
  const isEmbroidery = product ? isEmbroideryProduct(product) : false;
  const productColors = useMemo(
    () =>
      [...(product?.colors ?? [])].sort((a, b) => a.sortOrder - b.sortOrder),
    [product]
  );
  const selectedColorId =
    colorChoice &&
    (colorChoice.colorId === null ||
      colorChoice.colorId === NEON_COLOR.id ||
      colorChoice.productId === product?.id)
      ? colorChoice.colorId
      : NEON_COLOR.id;
  const previewColor =
    selectedColorId === NEON_COLOR.id
      ? NEON_COLOR
      : productColors.find((c) => c.id === selectedColorId);
  const previewHex = previewColor?.hexCode ?? null;
  const cutoutMaskUrl = editorCutoutMaskUrl(entry?.template);
  // A template without cutout mask cannot be coloured (the creator cannot
  // either); its shading map is see-through, so the colour goes behind it and
  // shows through the garment. With a mask the stage stays neutral.
  const stageBackground = cutoutMaskUrl ? NEUTRAL_STAGE_BG : previewHex;

  // Rebuild the draft the moment the active entry changes — during render, not
  // in an effect. An effect would lag one frame, so the editor would paint the
  // new mockup with the previous entry's rect. React re-renders synchronously
  // after a render-phase setState and commits only the final result, so this
  // guarded reset is flicker-free (the standard "adjust state on prop change").
  const entryKey = entry ? `${entry.position.id}:${entry.template.id}` : null;
  // The key lives in state, not in a ref: a ref written during render
  // survives StrictMode's discarded first render while that render's
  // setState calls are dropped, which left the editor without a draft.
  const [draftKey, setDraftKey] = useState<string | null>(null);
  if (entryKey !== draftKey) {
    setDraftKey(entryKey);
    setSaveError(null);
    if (entry) {
      const next = buildDraft(
        entry.position,
        entry.template,
        mockupWidth,
        mockupHeight
      );
      setDraft(next);
      setBaseline(next);
    } else {
      setDraft(null);
      setBaseline(null);
    }
  }

  // Read by the async save to tell whether the admin moved on meanwhile.
  const draftKeyRef = useRef<string | null>(draftKey);
  useEffect(() => {
    draftKeyRef.current = draftKey;
  }, [draftKey]);

  // Moving to another product restarts that product's queue at its first
  // mockup. Under a calibration filter it starts at the first default
  // mockup with that status instead, once the product's mockups are loaded:
  // the queue lists a product when any default template matches, and mockup
  // 1 may well be a calibrated one.
  const calibrationStartFor = useRef<string | null>(null);
  useEffect(() => {
    setEntryIndex(0);
    calibrationStartFor.current =
      filters.calibration !== 'any' ? (currentProduct?.id ?? null) : null;
    // Only a new product picks a start mockup.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProduct?.id]);
  useEffect(() => {
    const productId = calibrationStartFor.current;
    if (!productId || product?.id !== productId || entries.length === 0) {
      return;
    }
    calibrationStartFor.current = null;
    // A shared link names its own mockup (step 2 below).
    if (pendingLink || filters.calibration === 'any') return;
    const index = entries.findIndex(
      (e) =>
        e.template.isDefault !== false &&
        (filters.calibration === 'open'
          ? isTemplateOpen(e.position, e.template)
          : templateFrameStatus(e.position.printArea, e.template) ===
            filters.calibration)
    );
    if (index > 0) setEntryIndex(index);
  }, [product, entries, pendingLink, filters.calibration]);

  // Shared link, step 1: find the product. Declared after the reset above so
  // step 2 can still pick the mockup within the same commit.
  const linkSkuToResolve =
    pendingLink && !pendingLink.productId ? pendingLink.sku : null;
  useEffect(() => {
    if (!linkSkuToResolve || products.length === 0) return;
    let cancelled = false;
    void resolveLinkedProduct(
      products,
      linkSkuToResolve,
      pendingLink?.mockup ?? null,
      async (id) =>
        (await adminApi.getAdminProduct(id)).mockupTemplates?.map(
          (t) => t.id
        ) ?? []
    )
      .then((index) => {
        if (cancelled) return;
        if (index < 0) {
          // Not in a status-filtered queue (typically a deactivated product
          // under the default "Nur aktive"): widen the status and look
          // again in the new queue; the link stays open meanwhile.
          if (filters.status !== 'all') {
            toast(
              `SKU ${linkSkuToResolve} ist nicht unter „${
                filters.status === 'active' ? 'Nur aktive' : 'Nur deaktivierte'
              }“, zeige alle Produkte`
            );
            setSearchParams(
              (previous) =>
                writeFrameReviewFilters(previous, {
                  ...parseFrameReviewFilters(previous),
                  status: 'all',
                }),
              { replace: true }
            );
            return;
          }
          toast.error(`SKU ${linkSkuToResolve} ist nicht im Durchlauf`);
          setPendingLink(null);
          return;
        }
        setProductIndex(index);
        setPendingLink((prev) =>
          prev ? { ...prev, productId: products[index]!.id } : prev
        );
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        toast.error(getErrorMessage(error));
        setPendingLink(null);
      });
    return () => {
      cancelled = true;
    };
    // pendingLink.mockup only matters together with a new SKU; the status
    // is read when the queue arrives, which a status change also brings.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkSkuToResolve, products]);

  // Shared link, step 2: once the product's mockups are loaded, open the one
  // the link names.
  useEffect(() => {
    if (!pendingLink?.productId || product?.id !== pendingLink.productId) {
      return;
    }
    const index = pendingLink.mockup
      ? entries.findIndex((e) => e.template.id === pendingLink.mockup)
      : 0;
    if (index < 0) toast.error('Mockup aus dem Link nicht gefunden');
    setEntryIndex(Math.max(index, 0));
    setPendingLink(null);
  }, [pendingLink, product, entries]);

  // Keep the URL on the mockup on screen, so the address bar is always a
  // link to exactly this view.
  const onScreenProduct =
    currentProduct && product?.id === currentProduct.id ? currentProduct : null;
  // A product without a mockup to show still gets its own SKU (and no
  // mockup), so the address never names the previous product.
  useEffect(() => {
    if (pendingLink || !onScreenProduct) return;
    const sku = linkSku(onScreenProduct);
    const mockup = entry?.template.id ?? null;
    if (
      searchParams.get('sku') === sku &&
      searchParams.get('mockup') === mockup
    ) {
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.set('sku', sku);
    if (mockup) next.set('mockup', mockup);
    else next.delete('mockup');
    setSearchParams(next, { replace: true });
  }, [pendingLink, onScreenProduct, entry, searchParams, setSearchParams]);

  const copyLink = useCallback(() => {
    void navigator.clipboard
      .writeText(window.location.href)
      .then(() => toast.success('Link kopiert'))
      .catch(() => toast.error('Link konnte nicht kopiert werden'));
  }, []);

  const calibrateTemplate = useCalibrateTemplate(currentProduct?.id ?? '');
  // The last save's answer said the product is not selectable (R13).
  const [notSelectable, setNotSelectable] = useState<{
    productId: string;
    warning: string | null;
  } | null>(null);

  const bounds: AreaBounds = { mockupWidth, mockupHeight };

  // Snap the frame onto a mockup centre line without touching its size.
  const applyCenter = useCallback(
    (axis: 'x' | 'y' | 'both') => {
      setDraft((prev) =>
        prev ? { ...prev, ...centerArea(prev, bounds, axis) } : prev
      );
    },
    // bounds is derived from the active entry's mockup size; entryKey captures it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [entryKey]
  );

  // Back to the stored frame; the carry-over buttons save on their own.
  const discardDraft = useCallback(() => setDraft(baseline), [baseline]);

  // The height follows the width and the millimetres (R8), on every edit.
  const updateFrame = useCallback(
    (patch: Partial<EditableArea>) => {
      setDraft((prev) => {
        if (!prev) return prev;
        const next = { ...prev, ...patch };
        return { ...next, ...frameFromDrawn(next, next, mockupWidth) };
      });
    },
    [mockupWidth]
  );
  const setField = useCallback(
    (field: keyof EditableArea, value: number) =>
      updateFrame({ [field]: value }),
    [updateFrame]
  );

  // Carry-over (the adaptation rule): how big a design from another product
  // lands here. It is the product's rule, so the buttons save it on the
  // product at once; the frame itself is untouched. The preview fits the
  // sample design into the same box the carry-over computes.
  const saveCarryOver = useSetProductCarryOver(currentProduct?.id ?? '');
  const applyCarryOver = useCallback(
    async (patch: {
      carryOverPercent?: number | null;
      carryOverAnchor?: CarryOverAnchor | null;
    }) => {
      if (!product) return;
      try {
        await saveCarryOver.mutateAsync({
          productClass: product.productClass ?? null,
          carryOverPercent: product.carryOverPercent ?? null,
          carryOverAnchor: product.carryOverAnchor ?? null,
          carryOverTopOffsetMm: product.carryOverTopOffsetMm ?? null,
          ...patch,
        });
        toast.success('Adaptionsregel gespeichert');
      } catch (error) {
        toast.error(getErrorMessage(error));
      }
    },
    [product, saveCarryOver]
  );
  const carryOver = product ? effectiveCarryOver(product, draft) : null;
  const percentBox =
    product && draft && draft.sizeTierOverride !== 'RUNDUM'
      ? targetPercentBox({
          ...carryOver,
          sizeTierOverride: draft.sizeTierOverride,
        })
      : null;
  const sampleDesignBox = draft ? sampleDesignRect(draft, percentBox) : null;
  const areaHasException = draft?.carryOverPercent != null;

  const dirty = !!draft && !!baseline && !draftsEqual(draft, baseline);
  // Calibrating needs both millimetre values of the position (R9).
  const missingField = draft ? missingMmField(draft) : null;
  // A moved frame, a start suggestion and a draft to review are saved; a
  // calibrated frame left as it is only moves on.
  const needsSave = dirty || frameSource !== 'calibration';
  const saving = calibrateTemplate.isPending;

  const totalProducts = products.length;
  const atFirst = productIndex === 0 && entryIndex === 0;
  const atLast =
    productIndex >= totalProducts - 1 && entryIndex >= entries.length - 1;

  const goNext = useCallback(() => {
    if (entryIndex < entries.length - 1) {
      setEntryIndex((i) => i + 1);
    } else if (productIndex < totalProducts - 1) {
      setEntryIndex(0); // start the next product at its first mockup
      setProductIndex((i) => i + 1);
    }
  }, [entryIndex, entries.length, productIndex, totalProducts]);

  const goPrev = useCallback(() => {
    if (entryIndex > 0) {
      setEntryIndex((i) => i - 1);
    } else if (productIndex > 0) {
      // Jump to the previous product (its first mockup; the queue length is
      // only known once it loads).
      setEntryIndex(0);
      setProductIndex((i) => i - 1);
    }
  }, [entryIndex, productIndex]);

  const saveAndNext = useCallback(
    async (options: { confirmDraft?: boolean } = {}) => {
      if (!entry || !draft || !baseline || !currentProduct) return;
      if (!needsSave && !options.confirmDraft) {
        goNext();
        return;
      }
      if (missingField) return;
      const savedEntry = entry;
      // "Entwurf übernehmen" confirms the stored draft as it is.
      const savedDraft = options.confirmDraft ? baseline : draft;
      const savedProductId = currentProduct.id;
      const wasOpen = isTemplateOpen(savedEntry.position, savedEntry.template);
      const keyAtSave = entryKey;
      setSaveError(null);
      try {
        const result = await calibrateTemplate.mutateAsync({
          templateId: savedEntry.template.id,
          payload: {
            x: savedDraft.x,
            y: savedDraft.y,
            width: savedDraft.width,
            ...(options.confirmDraft && { confirmDraft: true }),
          },
        });
        const hint = `${savedEntry.template.name} · ${calibrationSavedMessage(result)}`;
        setSavedHint(hint);
        toast.success(hint, { position: 'bottom-right' });
        setNotSelectable(
          result.productSelectable
            ? null
            : { productId: savedProductId, warning: result.warning }
        );
        // The product line shows "Rahmen geändert" and the counter the open
        // templates; stamp the cached queue instead of refetching, so a
        // product saved under "noch nie" or "offen" stays in the walkthrough
        // until the admin changes the filters.
        queryClient.setQueriesData<adminApi.FrameReviewQueueResult>(
          { queryKey: ['frame-review', 'queue'] },
          (cached) =>
            cached && {
              ...cached,
              items: cached.items.map((item) =>
                item.id === savedProductId
                  ? {
                      ...item,
                      frameChangedAt: new Date().toISOString(),
                      templatesToCalibrate: Math.max(
                        0,
                        item.templatesToCalibrate - (wasOpen ? 1 : 0)
                      ),
                    }
                  : item
              ),
            }
        );
        // The mutation is async; only mark saved and advance if the admin
        // hasn't navigated to another entry meanwhile, or a late
        // baseline/next would clobber the entry now on screen.
        if (draftKeyRef.current === keyAtSave) {
          setBaseline(savedDraft);
          goNext();
        }
      } catch (error) {
        const message = calibrationErrorMessage(error);
        setSaveError(message);
        toast.error(message, { position: 'bottom-right' });
      }
    },
    [
      entry,
      draft,
      baseline,
      currentProduct,
      needsSave,
      missingField,
      entryKey,
      calibrateTemplate,
      goNext,
      queryClient,
    ]
  );

  // Discard guard, mirroring the per-product editor. Skipping or stepping back
  // with unsaved edits drops them, so ask first.
  const confirmDiscard = useCallback(
    () => !dirty || window.confirm('Ungespeicherte Änderungen verwerfen?'),
    [dirty]
  );
  const handleBack = useCallback(() => {
    if (confirmDiscard()) goPrev();
  }, [confirmDiscard, goPrev]);
  const handleSkip = useCallback(() => {
    if (confirmDiscard()) goNext();
  }, [confirmDiscard, goNext]);

  // Filters rebuild the queue; the product on screen may drop out of it, so
  // unsaved edits ask first. A change is a patch merged into the filters
  // the URL holds at that moment, so a debounced field never writes stale
  // values over a select changed meanwhile. The link params stay as well.
  const applyFilters = useCallback(
    (patch: Partial<FrameReviewFilters>): boolean => {
      if (!confirmDiscard()) return false;
      setSearchParams(
        (previous) =>
          writeFrameReviewFilters(previous, {
            ...parseFrameReviewFilters(previous),
            ...patch,
          }),
        { replace: true }
      );
      return true;
    },
    [confirmDiscard, setSearchParams]
  );

  // Browser-level guard: warn before closing/reloading with unsaved edits.
  useEffect(() => {
    if (!dirty) return;
    const handler = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  // Router-level guard: covers leaving the walkthrough (e.g. the link back to
  // the product list) and browser back/forward.
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      dirty && currentLocation.pathname !== nextLocation.pathname
  );
  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    if (window.confirm('Ungespeicherte Änderungen verwerfen?')) {
      blocker.proceed();
    } else {
      blocker.reset();
    }
  }, [blocker]);

  if (listLoading) {
    return <LoadingState label="Produkte werden geladen…" />;
  }

  const header = (
    <>
      <div className="mb-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h1 className="text-lg font-bold text-ink">Rahmen-Durchlauf</h1>
        <span className="text-xs text-mute">
          Standard-Vorlagen zuerst, dann die übrigen · vorne → hinten → nächstes
          Produkt · Ziehen rastet mittig ein (Alt = frei)
        </span>
      </div>
      <FrameReviewFilterBar
        filters={filters}
        onChange={applyFilters}
        total={totalProducts}
        openTemplates={openTemplates}
        fetching={listFetching}
      />
    </>
  );

  if (totalProducts === 0) {
    return (
      <div className="mx-auto w-full max-w-7xl">
        {header}
        <Card className="mx-auto w-full max-w-3xl">
          <EmptyState
            title="Keine Produkte im Durchlauf"
            description="Kein Produkt mit Mockup-Vorlagen passt zu den Filtern."
          />
          <Link
            to="/admin/products"
            className="mt-3 inline-block text-sm font-medium text-ink-2 hover:text-ink"
          >
            ← Zu den Produkten
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-7xl">
      {header}

      <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
        {/* Left: the mockup gets the room; it is the thing you work on. */}
        <div className="min-w-0 flex-1">
          <Card className="p-2 sm:p-3">
            {productLoading ? (
              <LoadingState label="Mockups werden geladen…" />
            ) : !entry ? (
              <EmptyState
                title={onlyOpen ? 'Nichts offen' : 'Keine Flächen'}
                description={
                  onlyOpen
                    ? 'Alle Vorlagen dieses Produkts sind kalibriert. Weiter zum nächsten.'
                    : 'Dieses Produkt hat keine Position mit Mockup. Weiter zum nächsten.'
                }
              />
            ) : !baseImageUrl ? (
              <EmptyState
                title="Kein Mockup-Bild"
                description="Für dieses Template ist kein Bild hinterlegt."
              />
            ) : (
              draft && (
                <PrintAreaEditor
                  key={entryKey ?? 'none'}
                  baseImageUrl={baseImageUrl}
                  mockupWidth={mockupWidth}
                  mockupHeight={mockupHeight}
                  stageBackground={stageBackground}
                  renderMockup={(size) => (
                    <MockupRenderer
                      productId={product?.id}
                      template={uiTemplate}
                      // MockupRenderer paints the colour only inside a
                      // cutout mask; "ohne Farbe" leaves the bare shading.
                      backgroundColor={previewHex ?? 'transparent'}
                      isHeathered={previewColor?.isHeathered ?? false}
                      isEmbroidery={isEmbroidery}
                      simplified
                      // The large editor assets, as the creator canvas loads.
                      assetSize="rendering"
                      frameBackground="transparent"
                      width={size.width}
                      height={size.height}
                      priority="high"
                    />
                  )}
                  maxHeight="calc(100vh - 260px)"
                  fitMode="contain"
                  proportionalResize
                  enableMagnet
                  area={draft}
                  rotation={draft.rotation}
                  lockHeight={!missingField}
                  suggestion={frameSource === 'suggestion'}
                  showSampleDesign
                  sampleDesignBox={sampleDesignBox}
                  sampleDesignBoxLabel={
                    percentBox
                      ? `Übernahme ${percentBox.percent} %`
                      : 'Beispieldesign, ganze Fläche'
                  }
                  // The size and box chips cover the mockup; the side
                  // panel already shows the numbers.
                  showLabels={false}
                  onChange={(area: EditableArea) => updateFrame(area)}
                />
              )
            )}
          </Card>
        </div>

        {/* Right: progress, settings and navigation. On short screens this
            column scrolls on its own so the page itself never scrolls. */}
        <aside className="w-full flex-shrink-0 space-y-2 lg:max-h-[calc(100vh-180px)] lg:w-96 lg:overflow-y-auto lg:pr-1">
          {/* Progress / context */}
          <div className="rounded-card p-3 ring-1 ring-line">
            <div className="flex items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-1.5">
                <StatusPill variant="neutral">
                  Produkt {productIndex + 1} / {totalProducts}
                </StatusPill>
                {entry && entries.length > 0 && (
                  <StatusPill variant="neutral">
                    Mockup {entryIndex + 1} / {entries.length}
                  </StatusPill>
                )}
                {dirty && (
                  <StatusPill variant="warning">ungespeichert</StatusPill>
                )}
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={copyLink}
                disabled={!entry}
                title="Link zu diesem Mockup kopieren"
              >
                <Link2 className="h-4 w-4" aria-hidden="true" />
                Link
              </Button>
            </div>
            {currentProduct && (
              <p className="mt-1.5 font-medium leading-tight text-ink">
                {currentProduct.name}
                <span className="ml-1 font-mono text-xs text-mute">
                  {currentProduct.artNumber}
                </span>
                <StatusPill
                  variant={currentProduct.isActive ? 'published' : 'danger'}
                  className="ml-1.5 align-middle"
                >
                  {currentProduct.isActive ? 'aktiv' : 'deaktiviert'}
                </StatusPill>
                <span className="ml-1.5 text-xs font-normal text-mute">
                  Rahmen geändert:{' '}
                  {frameChangedLabel(currentProduct.frameChangedAt)}
                </span>
              </p>
            )}
            {currentProduct &&
              (currentProduct.templatesWithoutPosition ?? 0) > 0 && (
                <p
                  className="mt-1 text-xs text-danger"
                  data-testid="templates-without-position"
                >
                  {currentProduct.templatesWithoutPosition === 1
                    ? '1 Vorlage ohne passende Position (Datenfehler, nicht kalibrierbar)'
                    : `${currentProduct.templatesWithoutPosition} Vorlagen ohne passende Position (Datenfehler, nicht kalibrierbar)`}
                </p>
              )}
            {entry && (
              <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-mute">
                <StatusPill variant="neutral">{entry.position.name}</StatusPill>
                <span>
                  {entry.template.name}
                  {isDefaultTemplate ? ' · Standard' : ''}
                </span>
                <CalibrationStatusPill status={frameStatus} />
              </div>
            )}
          </div>

          {/* Settings: one labelled row each, so the column fits without
              scrolling. */}
          {entry && draft && (
            <div className="space-y-2 rounded-card p-3 ring-1 ring-line">
              <SettingRow
                label="Farbe"
                hint={
                  !cutoutMaskUrl
                    ? 'Vorlage ohne Ausschnittmaske: die Farbe liegt hinter der ganzen Vorlage; im Creator wird sie nicht dargestellt.'
                    : (previewColor?.name ?? 'Ohne Farbe')
                }
              >
                <button
                  type="button"
                  onClick={() =>
                    product &&
                    setColorChoice({ productId: product.id, colorId: null })
                  }
                  title="Ohne Farbe"
                  aria-label="Produktfarbe ohne Farbe"
                  aria-pressed={selectedColorId === null}
                  className={swatchClass(selectedColorId === null)}
                  style={CHECKER_SWATCH_STYLE}
                />
                {[NEON_COLOR, ...productColors].map((color) => (
                  <button
                    key={color.id}
                    type="button"
                    onClick={() =>
                      product &&
                      setColorChoice({
                        productId: product.id,
                        colorId: color.id,
                      })
                    }
                    title={color.name}
                    aria-label={`Produktfarbe ${color.name}`}
                    aria-pressed={selectedColorId === color.id}
                    className={swatchClass(selectedColorId === color.id)}
                    style={{ backgroundColor: color.hexCode }}
                  />
                ))}
              </SettingRow>

              <SettingRow label="Zentrieren">
                <Button
                  type="button"
                  variant="outline"
                  size="icon-sm"
                  onClick={() => applyCenter('x')}
                  title="Horizontal zentrieren"
                  aria-label="Horizontal zentrieren"
                >
                  <AlignCenterVertical className="h-4 w-4" aria-hidden="true" />
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="icon-sm"
                  onClick={() => applyCenter('y')}
                  title="Vertikal zentrieren"
                  aria-label="Vertikal zentrieren"
                >
                  <AlignCenterHorizontal
                    className="h-4 w-4"
                    aria-hidden="true"
                  />
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="icon-sm"
                  onClick={() => applyCenter('both')}
                  title="Beides zentrieren"
                  aria-label="Beides zentrieren"
                >
                  <Crosshair className="h-4 w-4" aria-hidden="true" />
                </Button>
              </SettingRow>

              <SettingRow
                label="Übernahme"
                hint={
                  <span data-testid="carry-over-status">
                    {percentBox
                      ? `Adaptionsregel am Produkt · gilt hier: ${percentBox.percent} % · ${
                          percentBox.anchor === 'TOP'
                            ? `oben, ${percentBox.topOffsetMm} mm Abstand`
                            : 'mittig'
                        } · speichert sofort`
                      : draft.sizeTierOverride === 'RUNDUM'
                        ? 'Rundum-Fläche: keine Prozent-Regel.'
                        : 'Keine Prozent-Regel: Standardregeln (Objekt → Textil 80 %, Textil → Objekt in mm des Originals).'}
                    {areaHasException &&
                      ` · Ausnahme dieser Fläche (${draft.carryOverPercent} %) geht vor, Pflege auf der Produktseite.`}
                  </span>
                }
              >
                {[80, 90, 100].map((preset) => (
                  <Button
                    key={preset}
                    type="button"
                    variant={
                      product?.carryOverPercent === preset
                        ? 'primary'
                        : 'outline'
                    }
                    size="sm"
                    className="px-2"
                    aria-pressed={product?.carryOverPercent === preset}
                    disabled={!product || saveCarryOver.isPending}
                    onClick={() =>
                      void applyCarryOver({ carryOverPercent: preset })
                    }
                  >
                    {preset} %
                  </Button>
                ))}
                <Button
                  type="button"
                  variant={
                    product && product.carryOverPercent == null
                      ? 'primary'
                      : 'outline'
                  }
                  size="sm"
                  className="px-2"
                  aria-pressed={!!product && product.carryOverPercent == null}
                  disabled={!product || saveCarryOver.isPending}
                  onClick={() =>
                    void applyCarryOver({ carryOverPercent: null })
                  }
                  title="Keine Prozent-Regel, es gelten die Standardregeln"
                >
                  Standard
                </Button>
                {(
                  [
                    ['CENTER', 'Mittig'],
                    ['TOP', 'Oben'],
                  ] as [CarryOverAnchor, string][]
                ).map(([anchor, label]) => (
                  <Button
                    key={anchor}
                    type="button"
                    variant={
                      product?.carryOverAnchor === anchor
                        ? 'primary'
                        : 'outline'
                    }
                    size="sm"
                    className="px-2"
                    aria-pressed={product?.carryOverAnchor === anchor}
                    disabled={!product || saveCarryOver.isPending}
                    onClick={() =>
                      void applyCarryOver({ carryOverAnchor: anchor })
                    }
                  >
                    {label}
                  </Button>
                ))}
              </SettingRow>

              <SettingRow
                label="Rahmen px"
                hint={
                  missingField ? (
                    <span
                      role="alert"
                      className="text-warning"
                      data-testid="missing-mm-hint"
                    >
                      Kalibrieren gesperrt: {MM_FIELD_LABEL[missingField]}{' '}
                      fehlt. Die mm auf der Produktseite pflegen.
                    </span>
                  ) : (
                    <>
                      {`= ${draft.maxWidthMm} × ${draft.maxHeightMm} mm · ${(
                        draft.width / (draft.maxWidthMm ?? 1)
                      ).toFixed(2)} px/mm · die Höhe folgt den mm`}
                      {frameSource === 'draft' && (
                        <span className="block">
                          Gespeicherter Entwurf: prüfen, dann übernehmen oder
                          neu ziehen.
                        </span>
                      )}
                    </>
                  )
                }
              >
                <div className="grid w-full grid-cols-4 gap-1.5">
                  {(
                    [
                      ['x', 'X'],
                      ['y', 'Y'],
                      ['width', 'Breite'],
                      ['height', 'Höhe'],
                    ] as [keyof EditableArea, string][]
                  ).map(([field, label]) => {
                    // Width/height must stay a positive size; a 0 (or empty
                    // input) would persist a degenerate rect — and the next
                    // proportional corner drag would compute NaN from it.
                    const isSize = field === 'width' || field === 'height';
                    return (
                      <label key={field} className="text-[10px] text-mute">
                        {label}
                        <Input
                          type="number"
                          min={isSize ? 1 : 0}
                          value={draft[field]}
                          aria-label={label}
                          className="h-8 px-2 text-xs"
                          // The height follows the mm (R8).
                          disabled={field === 'height' && !missingField}
                          onChange={(event) => {
                            const raw = Math.round(
                              Number(event.target.value) || 0
                            );
                            setField(field, isSize ? Math.max(1, raw) : raw);
                          }}
                        />
                      </label>
                    );
                  })}
                </div>
              </SettingRow>
            </div>
          )}

          {/* Navigation */}
          <div className="space-y-2 rounded-card p-3 ring-1 ring-line">
            <Button
              type="button"
              variant="primary"
              className="w-full"
              onClick={() => void saveAndNext()}
              loading={saving}
              loadingLabel="Speichert…"
              disabled={
                !entry || !draft || saving || (needsSave && !!missingField)
              }
            >
              <Check className="h-4 w-4" aria-hidden="true" />
              {needsSave ? 'Speichern & weiter' : 'Passt — weiter'}
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Button>
            {frameSource === 'draft' && (
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => void saveAndNext({ confirmDraft: true })}
                disabled={saving || dirty || !!missingField}
                title="Den gespeicherten Entwurf unverändert als Kalibrierung übernehmen"
              >
                Entwurf übernehmen
              </Button>
            )}
            {saveError && (
              <p
                role="alert"
                className="text-xs text-danger"
                data-testid="save-error"
              >
                {saveError}
              </p>
            )}
            {savedHint && !saveError && (
              <p
                role="status"
                className="text-xs text-mute"
                data-testid="save-hint"
              >
                {savedHint}
              </p>
            )}
            {notSelectable &&
              notSelectable.productId === currentProduct?.id && (
                <NotSelectableBanner warning={notSelectable.warning} />
              )}
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="flex-1"
                onClick={handleBack}
                disabled={atFirst || saving}
              >
                <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                Zurück
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="flex-1"
                onClick={discardDraft}
                disabled={!dirty || saving}
                title="Ungespeicherte Änderungen am Rahmen verwerfen"
              >
                <Undo2 className="h-4 w-4" aria-hidden="true" />
                Verwerfen
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="flex-1"
                onClick={handleSkip}
                disabled={atLast || saving}
                title="Ohne Speichern weiter"
              >
                <SkipForward className="h-4 w-4" aria-hidden="true" />
                Überspringen
              </Button>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
